import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { resolveStoreName } from '@/lib/display';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { buildWhatsAppUrl, cleanWhatsAppNumber, useStoreWhatsApp } from '@/hooks/useStoreWhatsApp';
import { useCart } from '@/hooks/useCart';
import AnnonceCard from '@/components/feature/marketplace/AnnonceCard';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import DetailProduitPage from '@/pages/public/DetailProduitPage';
import DetailServicePage from '@/pages/public/DetailServicePage';
import {
  fetchMarketplaceAnnonceById,
  fetchAnnonceGallery,
  fetchAnnonceSeller,
  fetchSellerOtherAnnonces,
  fetchVendorBookingServices,
  fetchCategoryNames,
  parseMediaList,
  isProductType,
  isServiceType,
  isAnnonceUnavailable,
  type MarketplaceAnnonce,
  type MarketplaceSeller,
} from '@/lib/marketplaceCatalog';

// ─────────────────────────────────────────────────────────────
// Fiche détaillée d'une annonce (marketplace mixte).
// Galerie photos, description complète, contact WhatsApp / téléphone
// et carte de localisation.
// ─────────────────────────────────────────────────────────────

function priceLabel(prix: string | null, display: (amount: number, from?: string) => string, native: string): string {
  const n = Number(prix);
  if (!prix || Number.isNaN(n) || n <= 0) return 'Prix à discuter';
  return display(n, native);
}

function typeMeta(type: string | null | undefined): { label: string; icon: string; className: string } {
  if (isProductType(type)) {
    return { label: 'Produit', icon: 'ri-shopping-bag-3-line', className: 'bg-primary-100 text-primary-700' };
  }
  if (isServiceType(type)) {
    return { label: 'Service', icon: 'ri-service-line', className: 'bg-secondary-100 text-secondary-900' };
  }
  return { label: (type || 'Annonce').trim(), icon: 'ri-megaphone-line', className: 'bg-accent-100 text-accent-900' };
}

export default function DetailAnnoncePage() {
  const { id } = useParams<{ id: string }>();
  const annonceId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { tenant, theme, isTenant, loading: tenantLoading } = useTenant();
  // Suivi de visite de la page annonce/service publique (vues, IP, géo, provenance).
  useThemePageTracking('annonce', tenant?.id ?? null, isTenant && !!tenant);
  const storeWhatsApp = useStoreWhatsApp();
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  // Prix affichés dans la devise choisie par le visiteur (taux du jour).
  const { display, original } = useDisplayCurrency(monaie);
  const storeName = resolveStoreName(tenant, theme);
  // Nature attendue par la route : /produit, /service ou /annonce.
  const routeKind = location.pathname.split('/').filter(Boolean)[0] || 'annonce';

  const [annonce, setAnnonce] = useState<MarketplaceAnnonce | null>(null);
  const [seller, setSeller] = useState<MarketplaceSeller | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [others, setOthers] = useState<MarketplaceAnnonce[]>([]);
  const [bookingServiceId, setBookingServiceId] = useState<number | null>(null);
  const [rating, setRating] = useState<VendorRating | null>(null);
  const [categoryChain, setCategoryChain] = useState<{ id: number; nom: string }[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!Number.isFinite(annonceId) || annonceId <= 0) {
      setLoading(false);
      return;
    }
    // On attend la résolution du tenant : sans quoi on pourrait charger un
    // contenu avant de connaître le commerce courant (fuite croisée).
    if (tenantLoading) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const marketplaceId = isTenant && tenant ? Number(tenant.id) : null;
        const found = await fetchMarketplaceAnnonceById(annonceId, marketplaceId);
        if (cancelled) return;
        setAnnonce(found);
        if (!found) return;
        const [gal, sellerInfo, otherList] = await Promise.all([
          fetchAnnonceGallery(annonceId),
          fetchAnnonceSeller(found.idvendeur),
          fetchSellerOtherAnnonces(found.idvendeur, annonceId, 4),
        ]);
        if (cancelled) return;
        setGallery(gal);
        setSeller(sellerInfo);
        setOthers(otherList);
        if (found.idvendeur) {
          const rated = await fetchVendorRatings([found.idvendeur]);
          if (!cancelled) setRating(rated[found.idvendeur] ?? { average: 0, count: 0 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [annonceId, isTenant, tenant, tenantLoading]);

  useEffect(() => {
    document.title = annonce ? `${annonce.titre} — ${storeName}` : `Annonce — ${storeName}`;
  }, [annonce, storeName]);

  // Chaîne de catégories complète : Catégorie › Sous-catégorie › Sous-sous-catégorie.
  // L'ordre du plus général (grand-parent) au plus précis (catégorie sélectionnée).
  useEffect(() => {
    if (!annonce) {
      setCategoryChain([]);
      return;
    }
    const ordered = [annonce.idcategorie_grandparent, annonce.idcategorie_parent, annonce.idcategorie]
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ordered.length === 0) {
      setCategoryChain([]);
      return;
    }
    let cancelled = false;
    fetchCategoryNames(ordered).then((names) => {
      if (cancelled) return;
      setCategoryChain(ordered.filter((id) => names[id]).map((id) => ({ id, nom: names[id] })));
    }).catch(() => { if (!cancelled) setCategoryChain([]); });
    return () => { cancelled = true; };
  }, [annonce]);

  // Service de réservation lié (pour les contenus de type « service »).
  useEffect(() => {
    if (!annonce || !isServiceType(annonce.type) || !annonce.idvendeur) {
      setBookingServiceId(null);
      return;
    }
    let cancelled = false;
    fetchVendorBookingServices(annonce.idvendeur)
      .then((list) => {
        if (cancelled || list.length === 0) return;
        const exact = list.find(
          (b) => b.service_name?.toLowerCase() === annonce.titre?.toLowerCase(),
        );
        setBookingServiceId((exact || list[0]).id);
      })
      .catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, [annonce]);

  // Images : visuel principal + galerie additionnelle, dédupliqués.
  const images = useMemo(() => {
    const all = [annonce?.image, ...gallery]
      .map((v) => resolveMediaUrl(v))
      .filter(Boolean) as string[];
    return [...new Set(all)];
  }, [annonce?.image, gallery]);

  const contactPhone = (annonce?.telephone || seller?.telephone || '').trim();
  const videos = useMemo(() => parseMediaList(annonce?.video), [annonce?.video]);
  const whatsappNumber = cleanWhatsAppNumber(contactPhone) || storeWhatsApp.number;
  const whatsappMessage = annonce
    ? `Bonjour, je suis intéressé(e) par votre annonce « ${annonce.titre} »${Number(annonce.prix) > 0 ? ` (${priceLabel(annonce.prix, display, monaie)})` : ''}. Est-elle toujours disponible ?`
    : 'Bonjour, je vous contacte au sujet de votre annonce.';
  const whatsappUrl = buildWhatsAppUrl(whatsappNumber, whatsappMessage);

  // Nature du contenu : pilote les actions proposées.
  const isProduct = isProductType(annonce?.type);
  const isService = isServiceType(annonce?.type);
  const unavailable = isAnnonceUnavailable(annonce);
  const priceValue = Number(annonce?.prix || 0);

  const addProductToCart = () => {
    if (!annonce) return;
    addToCart({
      id: annonce.id,
      name: annonce.titre,
      price: priceValue,
      original_price: null,
      media: images[0] || resolveMediaUrl(annonce.image) || '',
      stock: 9999,
      product_type: 'physical',
      owner_id: annonce.idvendeur ?? undefined,
      owner_name: seller?.nomcommerce || undefined,
      currency: monaie,
    }, quantity);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2000);
  };

  const buyNow = () => {
    addProductToCart();
    navigate('/checkout');
  };

  const sectionLink = isProduct
    ? { to: '/produits', label: 'Produits' }
    : isService
      ? { to: '/services', label: 'Services' }
      : { to: '/annonces', label: 'Annonces' };

  const locationLabel = [annonce?.ville || seller?.ville, annonce?.pays || seller?.pays].filter(Boolean).join(', ');
  const mapQuery = locationLabel || storeName;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (!annonce) {
    // Repli : la fiche provient d'une source historique (produits / services
    // du catalogue marchand). On délègue aux pages dédiées quand aucun contenu
    // marketplace ne correspond à l'identifiant.
    if (routeKind === 'produit') return <DetailProduitPage />;
    if (routeKind === 'service') return <DetailServicePage />;
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Annonce introuvable</h2>
        <p className="text-sm text-foreground-500 mb-6">Cette annonce n&apos;existe pas ou n&apos;est plus disponible.</p>
        <Link
          to="/annonces"
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          Voir les annonces
        </Link>
      </div>
    );
  }

  const badge = typeMeta(annonce.type);
  const sellerImg = resolveMediaUrl(seller?.image);

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-10">
        {/* Fil d'ariane */}
        <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={sectionLink.to} className="hover:text-foreground-700 no-underline whitespace-nowrap">{sectionLink.label}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600 truncate max-w-[220px]">{annonce.titre}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Galerie */}
          <div>
            <div className="relative aspect-square bg-background-100 rounded-xl overflow-hidden border border-background-200/70">
              {images[active] ? (
                <img src={images[active]} alt={annonce.titre} className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-image-line text-4xl text-foreground-300"></i>
                </div>
              )}
              <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize flex items-center gap-1 ${badge.className}`}>
                <i className={badge.icon}></i>
                {badge.label}
              </span>
              {annonce.is_boost && (
                <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-500 text-background-50 flex items-center gap-1">
                  <i className="ri-flashlight-fill"></i>
                  À la une
                </span>
              )}
              {unavailable && (
                <span className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500 text-white flex items-center gap-1">
                  <i className="ri-forbid-2-line"></i>
                  Indisponible
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                {images.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setActive(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                      active === i ? 'border-primary-500' : 'border-background-200/70 hover:border-background-300/60'
                    }`}
                  >
                    <img src={img} alt={`${annonce.titre} ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}

            {/* Vidéos courtes du contenu (produits & services) */}
            {videos.length > 0 && (
              <div className="mt-6">
                <h2 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-video-line text-primary-500"></i>
                  Vidéos
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {videos.map((v, i) => (
                    <video
                      key={`${v}-${i}`}
                      src={resolveMediaUrl(v)}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded-lg border border-background-200/70 bg-black aspect-video"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Infos */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">{annonce.titre}</h1>

            {/* Chaîne de catégories complète (Catégorie › Sous-catégorie › Sous-sous) */}
            {categoryChain.length > 0 && (
              <nav className="flex items-center gap-1.5 flex-wrap mb-4" aria-label="Catégories du contenu">
                {categoryChain.map((c, idx) => (
                  <span key={c.id} className="flex items-center gap-1.5">
                    {idx > 0 && <i className="ri-arrow-right-s-line text-foreground-300 text-sm"></i>}
                    <Link
                      to={`/marketplace/categorie/${c.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium no-underline hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-price-tag-3-line text-[10px]"></i>
                      {c.nom}
                    </Link>
                  </span>
                ))}
              </nav>
            )}

            <div className="flex items-end gap-3 mb-4">
              <span className="text-2xl md:text-3xl font-bold text-primary-600">{priceLabel(annonce.prix, display, monaie)}</span>
              {priceValue > 0 && original(priceValue, monaie) && (
                <span className="text-sm text-foreground-400 mb-1">{original(priceValue, monaie)}</span>
              )}
            </div>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
              {locationLabel && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-primary-500"></i>
                  {locationLabel}
                </span>
              )}
              {annonce.created && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Publiée le {new Date(annonce.created).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>

            {annonce.description && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-foreground-900 mb-2">Description</h2>
                <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{annonce.description}</p>
              </div>
            )}

            {/* Contact */}
            <div className="space-y-3">
              {/* Produit : achat direct (panier / commande) */}
              {isProduct && unavailable && (
                <div className="flex items-start gap-2 px-3.5 py-3 rounded-lg bg-red-50 text-red-600 text-sm">
                  <i className="ri-forbid-2-line mt-0.5"></i>
                  <span>Ce produit est momentanément en rupture de stock ou non disponible. Contactez le vendeur pour connaître sa disponibilité.</span>
                </div>
              )}
              {isProduct && priceValue > 0 && !unavailable && (
                <>
                  <div className="flex items-center justify-between gap-3 px-1">
                    <span className="text-sm font-medium text-foreground-700">Quantité</span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="w-9 h-9 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 transition-colors cursor-pointer"
                        aria-label="Diminuer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-10 text-center text-sm font-semibold text-foreground-900 tabular-nums">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                        className="w-9 h-9 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 transition-colors cursor-pointer"
                        aria-label="Augmenter"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={buyNow}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-flashlight-line"></i>
                    Commander en ligne
                  </button>
                  <button
                    type="button"
                    onClick={addProductToCart}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium bg-background-100 text-foreground-800 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {added ? (
                      <><i className="ri-check-line text-accent-600"></i>Ajouté au panier</>
                    ) : (
                      <><i className="ri-shopping-cart-line"></i>Ajouter au panier</>
                    )}
                  </button>
                </>
              )}

              {/* Service : bouton de réservation en ligne */}
              {isService && bookingServiceId && (
                <button
                  type="button"
                  onClick={() => navigate(`/booking/${bookingServiceId}`)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line"></i>
                  Réserver en ligne
                </button>
              )}

              {whatsappNumber && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-green-500 text-white hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Contacter sur WhatsApp
                </a>
              )}
              {contactPhone && (
                <a
                  href={`tel:${contactPhone}`}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-phone-line text-lg"></i>
                  Appeler {contactPhone}
                </a>
              )}
              <Link
                to={sectionLink.to}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium bg-background-100 text-foreground-800 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap no-underline"
              >
                <i className="ri-arrow-left-line"></i>
                Retour aux {sectionLink.label.toLowerCase()}
              </Link>
            </div>

            {/* Vendeur */}
            {seller && (
              <Link
                to={`/boutique/${seller.userId}`}
                className="mt-6 pt-5 border-t border-background-200/70 flex items-center gap-3 no-underline hover:opacity-90 transition-opacity cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {sellerImg ? (
                    <img src={sellerImg} alt={seller.nomcommerce} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-foreground-400">
                      {seller.nomcommerce.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground-900 truncate">{seller.nomcommerce}</div>
                  <div className="text-[11px] text-foreground-400 flex items-center gap-1">
                    <i className="ri-store-2-line"></i>
                    Vendeur sur {storeName}
                  </div>
                  {rating && (rating.average > 0 || rating.count > 0) && (
                    <div className="mt-0.5">
                      <VendorRatingBadge average={rating.average} count={rating.count} />
                    </div>
                  )}
                </div>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </Link>
            )}
          </div>
        </div>

        {/* Carte de localisation */}
        <section className="mt-12">
          <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-map-pin-2-line text-primary-500"></i>
            Localisation
          </h2>
          <div className="rounded-xl overflow-hidden border border-background-200/70 bg-background-100">
            <iframe
              title={`Localisation de ${annonce.titre}`}
              src={mapSrc}
              className="w-full h-[280px] md:h-[360px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          {locationLabel && (
            <p className="mt-2 text-xs text-foreground-500 flex items-center gap-1.5">
              <i className="ri-map-pin-line"></i>
              {locationLabel}
            </p>
          )}
        </section>

        {/* Autres annonces du vendeur */}
        {others.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-store-2-line text-primary-500"></i>
              Autres annonces de {seller?.nomcommerce || 'ce vendeur'}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
              {others.map((a) => (
                <AnnonceCard key={a.id} annonce={a} monaie={monaie} sellerName={seller?.nomcommerce} sellerRating={rating} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}